// Compare slider module
// Converts {{compare}} ... {{/compare}} to a before/after image comparison slider
// Optional labels: {{compare: Before | After}}
//
// Usage:
//   {{compare}}
//   [Ghost image — "before"]
//   [Ghost image — "after"]
//   {{/compare}}
//
//   With labels:
//   {{compare: GTO | Human}}
//   [image 1]
//   [image 2]
//   {{/compare}}

/**
 * @param {Element}  container
 */
export function processCompareSliderBlocks(container) {
  const allElements = Array.from(container.children);
  const startRegex  = /\{\{compare(?::\s*([^}]*))?\}\}/;
  const endRegex    = /\{\{\/compare\}\}/;

  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const startMatch = (element.textContent || '').match(startRegex);
    if (!startMatch) continue;

    // Parse optional "Before | After" labels
    const labelStr = startMatch[1] || '';
    const labelParts = labelStr.split('|').map(s => s.trim());
    const labelBefore = labelParts[0] || '';
    const labelAfter  = labelParts[1] || '';

    let endIndex = -1, endElement = null;
    for (let j = i + 1; j < allElements.length; j++) {
      if ((allElements[j].textContent || '').match(endRegex)) {
        endIndex = j; endElement = allElements[j]; break;
      }
    }

    if (endIndex === -1) {
      console.warn('[Compare Slider] {{compare}} has no closing {{/compare}}.');
      continue;
    }

    // Collect all img elements between the markers
    const imgs = [];
    for (let k = i + 1; k < endIndex; k++) {
      const el = allElements[k];
      if (!el) continue;
      const img = el.tagName === 'IMG' ? el : el.querySelector('img');
      if (img) imgs.push(img.cloneNode(true));
    }

    if (imgs.length < 2) {
      console.warn('[Compare Slider] {{compare}} needs exactly 2 images inside.');
      for (let k = endIndex; k >= i; k--) {
        allElements[k]?.parentNode?.removeChild(allElements[k]);
      }
      continue;
    }

    const [imgBefore, imgAfter] = imgs;

    // ── Build DOM ────────────────────────────────────────────────────────────────

    const wrapper = document.createElement('div');
    wrapper.className = 'compare-slider__wrapper';

    // After — base layer
    imgAfter.className = 'compare-slider__after';
    wrapper.appendChild(imgAfter);

    // Before — clipped overlay
    const beforeDiv = document.createElement('div');
    beforeDiv.className = 'compare-slider__before';
    imgBefore.className = '';
    beforeDiv.appendChild(imgBefore);
    wrapper.appendChild(beforeDiv);

    // Labels
    if (labelBefore) {
      const lbl = document.createElement('span');
      lbl.className = 'compare-slider__label compare-slider__label--before';
      lbl.textContent = labelBefore;
      beforeDiv.appendChild(lbl);
    }
    if (labelAfter) {
      const lbl = document.createElement('span');
      lbl.className = 'compare-slider__label compare-slider__label--after';
      lbl.textContent = labelAfter;
      wrapper.appendChild(lbl);
    }

    // Handle
    const handle = document.createElement('div');
    handle.className = 'compare-slider__handle';
    handle.innerHTML = '<div class="compare-slider__line"></div><div class="compare-slider__grip"></div>';
    wrapper.appendChild(handle);

    const slider = document.createElement('div');
    slider.className = 'compare-slider';
    slider.appendChild(wrapper);

    element.replaceWith(slider);

    for (let k = i + 1; k <= endIndex; k++) {
      const el = allElements[k];
      if (el && el.parentNode === container) container.removeChild(el);
    }

    _initSliderInteraction(wrapper, beforeDiv, imgBefore, handle);

    i = endIndex;
  }
}

function _initSliderInteraction(wrapper, beforeDiv, imgBefore, handle) {
  let pos = 50; // percent

  function setPosition(pct) {
    pos = Math.max(0, Math.min(100, pct));
    beforeDiv.style.width = pos + '%';
    // Keep the before-image at full wrapper width so it doesn't compress
    imgBefore.style.width = wrapper.offsetWidth + 'px';
    handle.style.left = pos + '%';
  }

  function getPercent(clientX) {
    const rect = wrapper.getBoundingClientRect();
    return ((clientX - rect.left) / rect.width) * 100;
  }

  // Mouse
  wrapper.addEventListener('mousedown', e => {
    e.preventDefault();
    setPosition(getPercent(e.clientX));

    const onMove = e => setPosition(getPercent(e.clientX));
    const onUp   = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  });

  // Touch
  wrapper.addEventListener('touchstart', e => {
    const touch = e.touches[0];
    setPosition(getPercent(touch.clientX));

    const onMove = e => {
      e.preventDefault();
      setPosition(getPercent(e.touches[0].clientX));
    };
    const onEnd = () => {
      wrapper.removeEventListener('touchmove', onMove);
      wrapper.removeEventListener('touchend', onEnd);
    };
    wrapper.addEventListener('touchmove', onMove, { passive: false });
    wrapper.addEventListener('touchend', onEnd);
  }, { passive: true });

  // Set initial position after images load (need real dimensions)
  const afterImg = wrapper.querySelector('.compare-slider__after');
  if (afterImg && !afterImg.complete) {
    afterImg.addEventListener('load', () => setPosition(pos), { once: true });
  } else {
    requestAnimationFrame(() => setPosition(pos));
  }

  // Re-sync on resize
  window.addEventListener('resize', () => setPosition(pos));
}
